import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

import PageHero from "@/components/layout/PageHero";
import { VisualSection } from "@/components/layout/visualLayout";
import VisualTileGrid from "@/components/layout/VisualTileGrid";
import SectionIntro from "@/components/layout/SectionIntro";
import Pill from "@/components/common/Pill";
import Reveal from "@/components/common/Reveal";
import MagneticButton from "@/components/motion/MagneticButton";
import ASSETS from "@/lib/assets";

export default function About() {
  const { t } = useTranslation();
  const values = t("about.values", { returnObjects: true });

  const roles = [
    { title: t("about.role1Title"), body: t("about.role1Body") },
    { title: t("about.role2Title"), body: t("about.role2Body") },
    { title: t("about.role3Title"), body: t("about.role3Body") },
    { title: t("about.role4Title"), body: t("about.role4Body") },
    { title: t("about.role5Title"), body: t("about.role5Body") },
  ];

  return (
    <div data-testid="page-about">
      <PageHero
        eyebrow={t("about.eyebrow")}
        title={t("about.title")}
        subtitle={t("about.lead")}
        image={ASSETS.portrait}
        imageAlt="Fatma Acar"
        testId="about-hero"
      />

      <VisualSection testId="about-bio">
        <Reveal>
          <div className="max-w-3xl space-y-6">
            <p className="wit-body text-base">{t("about.bio1")}</p>
            <p className="wit-body text-base">{t("about.bio2")}</p>
            <p className="wit-body text-base">{t("about.bio3")}</p>
          </div>
        </Reveal>
      </VisualSection>

      <VisualSection testId="about-roles" divider ambient="mission">
        <SectionIntro eyebrow={t("about.rolesEyebrow")} />
        <VisualTileGrid
          items={roles.map(({ title, body }) => ({ title, body }))}
          testId="about-roles-grid"
          columns="sm:grid-cols-2 lg:grid-cols-3"
          staggerDelay={0.06}
        />
      </VisualSection>

      <VisualSection testId="about-values">
        <SectionIntro eyebrow={t("about.valuesEyebrow")} title={t("about.valuesTitle")} />
        <Reveal>
          <div className="flex flex-wrap gap-2" data-testid="values-pills">
            {Array.isArray(values) &&
              values.map((v, i) => (
                <Pill key={v} accent="bg-white" border="border-sandBeige" testId={`value-${i}`}>
                  {v}
                </Pill>
              ))}
          </div>
        </Reveal>
      </VisualSection>

      <VisualSection testId="about-moments" divider>
        <SectionIntro
          eyebrow={t("about.momentsEyebrow")}
          title={t("about.momentsTitle")}
          subtitle={t("about.momentsSubtitle")}
        />
        <VisualTileGrid
          variant="image"
          items={ASSETS.editorial.slice(0, 6).map((src, i) => ({
            image: src,
            testId: `moment-${i}`,
          }))}
          testId="moments-grid"
        />
      </VisualSection>

      <VisualSection testId="about-cta" tight divider ambient="newsletter">
        <Reveal>
          <div className="text-center max-w-lg mx-auto">
            <h2 className="wit-heading-section mb-3">{t("about.ctaTitle")}</h2>
            <p className="wit-body mb-8">{t("about.ctaBody")}</p>
            <MagneticButton as={Link} to="/contact" className="wit-btn-primary" testId="about-cta-button">
              {t("about.cta")} <ArrowRight size={15} />
            </MagneticButton>
          </div>
        </Reveal>
      </VisualSection>
    </div>
  );
}
